import "./OrderCard.scss";

import classNames from "classnames";
import { motion } from "framer-motion";
import { observer } from "mobx-react-lite";
import {
	FunctionComponent,
	MouseEvent,
	useEffect,
	useRef,
	useState,
} from "react";

import { RejectModalCards } from "@client/components/modals-ui/reject-modal-cards";
import ScheduledDate from "@client/components/reusable/scheduled-date/scheduled-date";
import SvgIcon from "@client/components/reusable/SvgIcons";
import { ReactComponent as CheckIconWhite } from "@client/components/reusable/SvgIcons/library/check_icon_white.svg";
import { useRootContext } from "@client/context/RootContext";
import { TDateISO } from "@client/interfaces";
import { ANIMATION_CONSTANTS, Order, OrderStatuses } from "@client/stores";

import { OrderCardDetails } from "./order-card-details";
import { OrderCardHeader } from "./order-card-header";
import { OrderInProcessing } from "./order-card-processing/order-card-processing";
import { OrderCardReady } from "./order-card-ready";

interface IOrderCardProps {
	order: Order;
	isPickingTab?: boolean;
	isReadyTab?: boolean;
	selected?: boolean;
	onSelectedChange?: (orderId: number) => void;
}

const OrderCard: FunctionComponent<IOrderCardProps> = ({
	order,
	isPickingTab,
	isReadyTab,
	selected,
	onSelectedChange,
}) => {
	const {
		modalStore,
		layoutStore: { ordersAnimation1, ordersAnimation2 },
		vendorStore,
	} = useRootContext();

	const orderCardRef = useRef<HTMLDivElement>(null);
	const [isDateModalVisible, setIsDateModalVisible] = useState(false);
	const [scheduledDate, setScheduledDate] = useState<TDateISO | undefined>(
		order.dates.scheduled_date
	);

	function handleScheduledDateChange(date: TDateISO | undefined) {
		setScheduledDate(date);
		vendorStore.setScheduledDate(order.id, date);
	}

	useEffect(() => {
		setScheduledDate(order.dates.scheduled_date);
	}, [order.dates.scheduled_date]);

	function openOrderCardModal(e: MouseEvent) {
		if (isDateModalVisible) {
			setIsDateModalVisible(false);
			return;
		}

		modalStore.addModal({
			id: "order-card-modal",
			showCloseBtn: true,
			content: (
				<div className="order-card-modal-container">
					<OrderCardHeader
						order={order}
						closeOrderCardModal={closeOrderCardModal}
						isOrderCardModal
					/>
					<OrderCardDetails
						order={order}
						orderCardRef={orderCardRef}
						toggleShowModal={show_cancelModal}
						closeOrderCardModal={closeOrderCardModal}
						isPickingTab={isPickingTab}
						isOrderCardModal
					/>
				</div>
			),
		});
	}

	const _animationParams = {
		// x: orderCardRef.current?.getBoundingClientRect().x || 0,
		y: orderCardRef.current?.getBoundingClientRect().y || 0,
		width: orderCardRef.current?.getBoundingClientRect().width || 0,
		height: orderCardRef.current?.getBoundingClientRect().height || 0,
		duration: 0.2,
		ease: "linear",
	};

	function show_cancelModal(e: MouseEvent) {
		e.preventDefault();
		e.stopPropagation();

		modalStore.addModal({
			id: "cancel-modal",
			content: (
				<RejectModalCards
					order={order}
					closeModal={hide_cancelModal}
					closeOrderCardModal={closeOrderCardModal}
				/>
			),
		});
	}

	function hide_cancelModal(e: MouseEvent) {
		e.preventDefault();
		e.stopPropagation();

		modalStore.removeModal("cancel-modal");
	}

	function closeOrderCardModal() {
		modalStore.removeModal("order-card-modal");
	}

	function handleDateModalState(value: boolean) {
		setIsDateModalVisible(value);
	}

	const animateAway = isReadyTab && ordersAnimation2.includes(order.id);

	return (
		<motion.div
			ref={orderCardRef}
			className={classNames("order-wrapper", { ["ready"]: isReadyTab })}
			initial={{
				x:
					order.status === OrderStatuses.Pending
						? 0
						: ANIMATION_CONSTANTS.ORDER_APPEARANCE.x,
				y:
					order.status === OrderStatuses.Pending
						? 0
						: ANIMATION_CONSTANTS.ORDER_APPEARANCE.y,
				opacity: 1,
			}}
			animate={{
				x: 0,
				y: animateAway ? -300 : 0,
				opacity: animateAway ? 0 : 1,
				height: animateAway ? 0 : "",
			}}
			transition={{
				ease: _animationParams.ease,
				duration: _animationParams.duration,
			}}
		>
			{(vendorStore.ordersInProcessing.includes(order.id) ||
				vendorStore.pullingDataInProgress) && <OrderInProcessing />}
			<div className="order-background" />

			{isReadyTab ? (
				<>
					{ordersAnimation1.includes(order.id) && (
						<motion.div
							className="order-background-green"
							initial={{ height: 0 }}
							animate={{ height: "100%" }}
							transition={{
								ease: _animationParams.ease,
								duration: _animationParams.duration,
							}}
						>
							<SvgIcon
								component={CheckIconWhite}
								className="check-icon-white"
							/>
						</motion.div>
					)}

					<OrderCardReady
						order={order}
						selected={selected}
						onSelectedChange={onSelectedChange}
					/>
				</>
			) : (
				<div className="order-container">
					<div className="order-container-inner" onClick={openOrderCardModal}>
						<OrderCardHeader
							order={order}
							isCollapsedCard
							isDateModalVisible={isDateModalVisible}
							handleDateModalState={handleDateModalState}
							isReadyTab={isReadyTab}
						/>
					</div>

					{isPickingTab &&
						vendorStore.vendorDetails?.delivery_dates_enabled && (
							<div className="order-scheduled-date-container">
								<div className="border-line" />

								<ScheduledDate
									scheduledDate={scheduledDate}
									setScheduledDate={handleScheduledDateChange}
								/>
							</div>
						)}
				</div>
			)}
		</motion.div>
	);
};

export default observer(OrderCard);
